import _ from "lodash";
import React from "react";
import { graphql, compose } from "lib/apolloClient";
import { connect } from "react-redux";
import {
  getOrganizationStaffQuery,
  getOrganizationStudentListQuery,
} from "modules/CommonQuery";
import {
  getOrganizationStaffFromCache,
  getOrganizationStudentListFromCache,
} from "modules/CommonGraphqlHelpers";
import {
  getProjectGroupStudentsPoolFromCache,
  getProjectGroupSupervisorsPoolFromCache,
} from "Projects/routes/Setup/routes/Invite/modules/InviteGraphqlHelpers";
import {
  getProjectGroupStudentsPoolQuery,
  getProjectGroupSupervisorsPoolQuery,
} from "Projects/routes/Setup/routes/Invite/modules/InviteQueries";
import {
  getCurrentProjectGroupDetails,
  getMemoizedNodeExtractedUsers,
} from "Projects/modules/ProjectModules";
import {
  getStaffListQuery,
  getCourseStaffGroupQuery,
} from "IBPlanner/modules/IBQuery";
import {
  getStaffListFromCache,
  getCourseStaffGroupFromCache,
} from "IBPlanner/modules/IBGraphqlHelpers";
import { getOrganizationCoursesStudents } from "Course/modules/CourseQuery";
import { getCourseAllStudentQuery } from "StudentPortfolio/modules/StudentPortfolioQuery";
import { getCourseAllStudentsFromCache } from "StudentPortfolio/modules/StudentPortfolioGraphqlHelpers";

import {
  getOrganizationStudentsFromCache,
  getYearGroupStudentsFromCache,
  getOrganizationStaffWithExternalUsersFromCache,
} from "Projects/modules/ProjectGraphqlHelpers";

import { getOrganizationCoursesStudentsFromCache } from "Course/modules/CourseGraphqlHelpers";
import {
  getOrganizationStudentsQuery,
  getYearGroupStudentsQuery,
  getOrganizationStaffWithExternalUsersQuery,
} from "Projects/modules/ProjectQueries";
import { getUserInfo } from "Login/modules/LoginModule/LoginUtils";
import { CAS_EXTERNAL_USER_MODULE_NAME } from "Projects/Constants/stringConstants";
import { ROLE_VISITOR } from "Constants/stringConstants";
import { getSelectedAcademicYearId } from "modules/CommonSelectors";

const SearchDropdownQueryDataHOC = WrappedComponent => {
  const WithSearchDropdownQueryData = props => {
    return <WrappedComponent {...props} />;
  };

  const filterHiddenCollaborators = ({ hideCollaborators, list }) => {
    return _.filter(list, item => !_.includes(hideCollaborators, item.id));
  };

  const getOrganizationStaffList = ({ data, hideCollaborators }) => {
    const supervisors = _.map(_.get(data, "node.staff.edges"), item => {
      return item?.node;
    });
    return filterHiddenCollaborators({ list: supervisors, hideCollaborators });
  };

  const getOrganizationStaffAndCASExternalSupervisorsList = ({
    data,
    hideCollaborators,
  }) => {
    const supervisors = _.map(_.get(data, "node.staff.edges"), item => {
      return item?.node;
    });
    const externalSupervisors = _.map(
      _.get(data, "node.externalUsers.edges"),
      item => {
        return item?.node;
      }
    );

    return filterHiddenCollaborators({
      list: supervisors.concat(externalSupervisors),
      hideCollaborators,
    });
  };

  const getProjectGroupStudentsList = ({ data, hideCollaborators }) => {
    const students = _.map(_.get(data, "node.students.edges"), item => {
      return item?.node.student;
    });
    return filterHiddenCollaborators({ list: students, hideCollaborators });
  };

  const getPortfolioStudents = ({ items, hideCollaborators }) => {
    const students = _.map(items, item => item.node);
    return filterHiddenCollaborators({ list: students, hideCollaborators });
  };

  const getStaffsList = ({ queryData, hideCollaborators }) => {
    const staffs = _.map(
      _.get(queryData, "platform.organization.staff.edges", []),
      staff => staff.node
    );
    return filterHiddenCollaborators({ list: staffs, hideCollaborators });
  };

  const getCourseList = ({ queryData }) => {
    const courses = _.get(queryData, "courses.edges", []);
    return _.map(courses, course => course.node);
  };

  const getProjectGroupSupervisorsList = ({ data, hideCollaborators }) => {
    const supervisors = _.map(_.get(data, "node.supervisors.edges"), item => {
      return item?.node?.staff;
    });

    return filterHiddenCollaborators({ list: supervisors, hideCollaborators });
  };

  const extractNodeFromItemList = ({ items, hideCollaborators }) => {
    const students = _.map(items, item => item.node);
    return filterHiddenCollaborators({ list: students, hideCollaborators });
  };

  const mapStateToProps = state => {
    const academicYearIds = _.compact([getSelectedAcademicYearId(state)]);
    const userInfo = getUserInfo();
    const organizationId = userInfo.org_id;
    const {
      type: projectGroupType,
      id: projectGroupId,
    } = getCurrentProjectGroupDetails({
      state,
    });

    const currentCurriculumProgramId = _.get(
      state,
      "platform.currentCurriculumProgram.id",
      ""
    );
    return {
      isData: true,
      isLoading: false,
      organizationId,
      currentCurriculumProgramId,
      projectGroupType,
      academicYearIds,
    };
  };

  return compose(
    connect(mapStateToProps),
    graphql(getProjectGroupStudentsPoolQuery, {
      name: "getProjectGroupStudentsPool",
      skip: ({ searchListTypes }) =>
        !_.includes(searchListTypes, "PROJECT_GROUP_STUDENTS_POOL"),
      options: ({ projectGroupId, searchText, academicYearIds }) => ({
        fetchPolicy: "cache-and-network",
        variables: {
          id: projectGroupId,
          filters: {
            searchText,
            academicYearIds,
          },
        },
      }),
      props({
        getProjectGroupStudentsPool,
        ownProps: { isLoading, isData, hideCollaborators },
      }) {
        const variables = _.get(getProjectGroupStudentsPool, "variables", {});
        const data = getProjectGroupStudentsPoolFromCache(variables);

        const studentList = getProjectGroupStudentsList({
          data,
          hideCollaborators,
        });

        return {
          studentList,
          isData: !_.isEmpty(data) && isData,
          isLoading:
            getProjectGroupStudentsPool["networkStatus"] == 1 ||
            getProjectGroupStudentsPool["networkStatus"] == 2 ||
            isLoading,
        };
      },
    }),
    graphql(getOrganizationStaffQuery, {
      name: "getOrganizationStaff",
      skip: ({ searchListTypes }) =>
        !_.includes(searchListTypes, "ORGANIZATION_STAFF"),
      options: ({ organizationId, searchText }) => ({
        fetchPolicy: "cache-and-network",
        variables: {
          organizationId,
          filters: {
            searchText,
          },
        },
      }),
      props({
        getOrganizationStaff,
        ownProps: {
          organizationId,
          isLoading,
          isData,
          searchText,
          hideCollaborators,
        },
      }) {
        const data = getOrganizationStaffFromCache({
          organizationId,
          filters: {
            searchText,
          },
        });

        const staffList = getOrganizationStaffList({
          data,
          hideCollaborators,
        });

        return {
          staffList,
          isData: !_.isEmpty(data) && isData,
          isLoading:
            getOrganizationStaff["networkStatus"] == 1 ||
            getOrganizationStaff["networkStatus"] == 2 ||
            isLoading,
        };
      },
    }),
    graphql(getOrganizationStaffWithExternalUsersQuery, {
      name: "getOrganizationStaffWithExternalUsers",
      skip: ({ searchListTypes }) =>
        !_.includes(
          searchListTypes,
          "ORGANIZATION_STAFF_WITH_EXTERNAL_SUPERVISORS"
        ),
      options: ({ organizationId, searchText }) => ({
        fetchPolicy: "cache-and-network",
        variables: {
          organizationId,
          filters: {
            searchText,
          },
          externalSupervisorFilters: {
            searchText,
            moduleNames: [CAS_EXTERNAL_USER_MODULE_NAME],
            roles: [ROLE_VISITOR],
          },
        },
      }),
      props({
        getOrganizationStaffWithExternalUsers,
        ownProps: { isLoading, isData, hideCollaborators },
      }) {
        const variables = _.get(
          getOrganizationStaffWithExternalUsers,
          "variables"
        );

        const data = getOrganizationStaffWithExternalUsersFromCache(variables);

        const staffList = getOrganizationStaffAndCASExternalSupervisorsList({
          data,
          hideCollaborators,
        });

        return {
          staffList,
          isData: !_.isEmpty(data) && isData,
          isLoading:
            getOrganizationStaffWithExternalUsers["networkStatus"] == 1 ||
            getOrganizationStaffWithExternalUsers["networkStatus"] == 2 ||
            isLoading,
        };
      },
    }),
    graphql(getStaffListQuery, {
      name: "getStaffList",
      skip: ({ searchListTypes }) => !_.includes(searchListTypes, "INDIVIDUAL"),
      options: ({
        searchText,
        supervisorGrades,
        curriculumProgramIds,
        portalType,
        courses,
        archivalFilters,
      }) => {
        return {
          fetchPolicy: "cache-and-network",
          variables: {
            searchText,
            portalType,
            courses,
            grades: supervisorGrades,
            curriculumProgramIds,
            ...archivalFilters,
          },
        };
      },
      props({
        getStaffList,
        ownProps: {
          isData,
          isLoading,
          searchText,
          curriculumProgramIds,
          supervisorGrades,
          portalType,
          courses,
          archivalFilters,
          hideCollaborators,
        },
      }) {
        const data = getStaffListFromCache({
          searchText,
          curriculumProgramIds,
          grades: supervisorGrades,
          portalType,
          courses,
          ...archivalFilters,
        });
        return {
          staffList: getStaffsList({ queryData: data, hideCollaborators }),
          isData: !_.isEmpty(data) && isData,
          isLoading:
            getStaffList["networkStatus"] == 1 ||
            getStaffList["networkStatus"] == 2 ||
            isLoading,
        };
      },
    }),
    // graphql(getStaffListQuery, {
    //   name: "getStaffList",
    //   skip: ({ searchListTypes }) => !_.includes(searchListTypes, "INDIVIDUAL"),
    //   options: ({ searchText, grades, portalType }) => {
    //     return {
    //       fetchPolicy: "cache-and-network",
    //       variables: { searchText, grades, portalType }
    //     };
    //   },
    //   props({ getStaffList, ownProps: { isData, isLoading } }) {
    //     return {
    //       staffList: getStaffsList({ queryData: getStaffList }),
    //       isData: getStaffList.platform && isData,
    //       isLoading:
    //         getStaffList["networkStatus"] == 1 ||
    //         getStaffList["networkStatus"] == 2 ||
    //         isLoading
    //     };
    //   }
    // }),
    graphql(getCourseStaffGroupQuery, {
      name: "getCourseStaffGroup",
      skip: ({ searchListTypes }) => !_.includes(searchListTypes, "GROUP"),
      options: ({
        searchText,
        supervisorGrades,
        portalType,
        curriculumProgramIds,
      }) => {
        return {
          fetchPolicy: "cache-and-network",
          variables: {
            searchText,
            portalType,
            grades: supervisorGrades,
            curriculumProgramIds,
          },
        };
      },
      props({
        getCourseStaffGroup,
        ownProps: {
          isData,
          isLoading,
          searchText,
          supervisorGrades,
          portalType,
          curriculumProgramIds,
        },
      }) {
        const data = getCourseStaffGroupFromCache({
          searchText,
          portalType,
          grades: supervisorGrades,
          curriculumProgramIds,
        });
        return {
          courseList: getCourseList({
            queryData: _.get(data, "platform.organization", {}),
          }),
          isData: !_.isEmpty(data) && isData,
          isLoading:
            getCourseStaffGroup["networkStatus"] == 1 ||
            getCourseStaffGroup["networkStatus"] == 2 ||
            isLoading,
        };
      },
    }),
    graphql(getOrganizationCoursesStudents, {
      name: "getCourseStudentGroup",
      skip: ({ searchListTypes }) =>
        !_.includes(searchListTypes, "STUDENT_GROUP"),
      options: ({ searchText, grades, organizationId }) => {
        return {
          fetchPolicy: "cache-and-network",
          variables: { searchText, grades, organizationId },
        };
      },
      props({
        getCourseStudentGroup,
        ownProps: { isData, isLoading, searchText, grades, organizationId },
      }) {
        const data = getOrganizationCoursesStudentsFromCache({
          searchText,
          grades,
          organizationId,
        });

        return {
          courseList: getCourseList({
            queryData: data,
          }),
          isData: !_.isEmpty(data) && isData,
          isLoading:
            getCourseStudentGroup["networkStatus"] == 1 ||
            getCourseStudentGroup["networkStatus"] == 2 ||
            isLoading,
        };
      },
    }),
    //TODO: changes this should fetch course from organization and add courseIds in filters
    graphql(getCourseAllStudentQuery, {
      name: "getCourseOfStudents",
      skip: ({ searchListTypes, courses }) =>
        !_.includes(searchListTypes, "COURSE_STUDENTS") ||
        _.isEmpty(_.first(courses)),
      options: ({ courses, searchText, portalType }) => ({
        fetchPolicy: "cache-and-network",
        variables: {
          id: _.first(courses),
          filters: { searchText, portalType },
        },
      }),
      props({
        getCourseOfStudents,
        ownProps: { isData, isLoading, courses, searchText, hideCollaborators },
      }) {
        const courseData = getCourseAllStudentsFromCache({
          id: _.first(courses),
          filters: { searchText },
        });
        return {
          getCourseOfStudents,
          studentList: extractNodeFromItemList({
            items: _.get(courseData, "students.edges", []),
            hideCollaborators,
          }),
          isData: !_.isEmpty(courseData) && isData,

          isLoading:
            getCourseOfStudents["networkStatus"] == 1 ||
            getCourseOfStudents["networkStatus"] == 2 ||
            isLoading,
        };
      },
    }),
    graphql(getYearGroupStudentsQuery, {
      name: "getYearGroupStudents",
      skip: ({ searchListTypes }) =>
        !_.includes(searchListTypes, "YEAR_GROUP_STUDENTS"),
      options: ({ yearGroupId }) => ({
        fetchPolicy: "cache-and-network",
        variables: { id: yearGroupId },
      }),
      props({
        getYearGroupStudents,
        ownProps: { yearGroupId, isData, isLoading },
      }) {
        const data = getYearGroupStudentsFromCache({ id: yearGroupId });
        const studentList = getMemoizedNodeExtractedUsers({
          userList: _.get(data, "node.students.edges", []),
        });

        return {
          isLoading:
            getYearGroupStudents["networkStatus"] == 1 ||
            getYearGroupStudents["networkStatus"] == 2 ||
            isLoading,
          isData: !_.isEmpty(data) && isData,
          studentList,
        };
      },
    }),
    graphql(getOrganizationStudentListQuery, {
      name: "getStudentList",
      skip: ({ searchListTypes }) =>
        !_.includes(searchListTypes, "ORGANISATION_STUDENTS"),
      options: ({ searchText, grades, courses, organizationId }) => {
        return {
          fetchPolicy: "cache-and-network",
          variables: { searchText, grades, courses, organizationId },
        };
      },
      props({
        getStudentList,
        ownProps: {
          isData,
          isLoading,
          searchText,
          grades,
          courses,
          organizationId,
          hideCollaborators,
        },
      }) {
        const queryData = getOrganizationStudentListFromCache({
          searchText,
          grades,
          courses,
          organizationId,
        });

        return {
          studentList: extractNodeFromItemList({
            items: _.get(queryData, "students.edges", []),
            hideCollaborators,
          }),
          isData: !_.isEmpty(queryData) && isData,
          isLoading:
            getStudentList["networkStatus"] == 1 ||
            getStudentList["networkStatus"] == 2 ||
            isLoading,
        };
      },
    }),
    graphql(getOrganizationStudentsQuery, {
      name: "getOrganizationStudents",
      skip: ({ searchListTypes }) =>
        !_.includes(searchListTypes, "PROJECT_GROUP_TYPE_STUDENTS"),
      options: ({
        organizationId,
        searchText,
        projectGroupType,
        currentCurriculumProgramId,
      }) => ({
        fetchPolicy: "cache-and-network",
        variables: {
          organizationId,
          filters: {
            projectGroupTypes: [projectGroupType],
            searchText,
            curriculumProgramIds: [currentCurriculumProgramId],
          },
        },
      }),
      props({
        getOrganizationStudents,
        ownProps: {
          isData,
          isLoading,
          hideCollaborators,
          searchText,
          organizationId,
          projectGroupType,
          currentCurriculumProgramId,
        },
      }) {
        const data = getOrganizationStudentsFromCache({
          organizationId,
          filters: {
            projectGroupTypes: [projectGroupType],
            searchText,
            curriculumProgramIds: [currentCurriculumProgramId],
          },
        });

        return {
          studentList: getPortfolioStudents({
            items: _.get(data, "node.students.edges", []),
            hideCollaborators,
          }),
          isData: !_.isEmpty(data) && isData,
          isLoading:
            _.includes([1, 2], getOrganizationStudents["networkStatus"]) ||
            isLoading,
        };
      },
    }),
    graphql(getProjectGroupSupervisorsPoolQuery, {
      name: "getProjectGroupSupervisors",
      skip: ({ searchListTypes }) =>
        !_.includes(searchListTypes, "PROJECT_GROUP_SUPERVISORS"),
      options: ({ projectGroupId, searchText, academicYearIds }) => ({
        fetchPolicy: "cache-and-network",
        variables: {
          projectGroupId,
          filters: {
            searchText,
            academicYearIds,
          },
        },
      }),
      props({
        getProjectGroupSupervisors,
        ownProps: {
          projectGroupId,
          isLoading,
          isData,
          searchText,
          hideCollaborators,
          academicYearIds,
        },
      }) {
        const filters = {
          searchText,
          academicYearIds,
        };

        const data = getProjectGroupSupervisorsPoolFromCache({
          projectGroupId,
          filters,
        });

        const staffList = getProjectGroupSupervisorsList({
          data,
          hideCollaborators,
        });

        return {
          staffList,
          isData: !_.isEmpty(data) && isData,
          isLoading:
            getProjectGroupSupervisors["networkStatus"] == 1 ||
            getProjectGroupSupervisors["networkStatus"] == 2 ||
            isLoading,
        };
      },
    }),
    graphql(getProjectGroupStudentsPoolQuery, {
      name: "getProjectGroupStudentsPool",
      skip: ({ searchListTypes }) =>
        !_.includes(searchListTypes, "PROJECT_GROUP_STUDENTS_POOL"),
      options: ({ projectGroupId, searchText, academicYearIds }) => ({
        fetchPolicy: "cache-and-network",
        variables: {
          id: projectGroupId,
          filters: {
            searchText,
            academicYearIds,
          },
        },
      }),
      props({
        getProjectGroupStudentsPool,
        ownProps: { isLoading, isData, hideCollaborators },
      }) {
        const variables = _.get(getProjectGroupStudentsPool, "variables", {});
        const data = getProjectGroupStudentsPoolFromCache(variables);

        const studentList = getProjectGroupStudentsList({
          data,
          hideCollaborators,
        });

        return {
          studentList,
          isData: !_.isEmpty(data) && isData,
          isLoading:
            getProjectGroupStudentsPool["networkStatus"] == 1 ||
            getProjectGroupStudentsPool["networkStatus"] == 2 ||
            isLoading,
        };
      },
    })
  )(WithSearchDropdownQueryData);
};

export default SearchDropdownQueryDataHOC;
